import { getCore } from '../../../client/core/getCore';
import { getDefaultClient } from '../../../client/defaultClient';
import { createApiClient } from '../../apiClient';
import { updateAuthFromVerifyResponse } from '../../auth/updateAuthFromVerifyResponse';
import type { OTPVerification } from '../../otp/otp.types';
import type { UpdateUserFields } from '../user.types';

type UpdateUserParams = {
  userFields: UpdateUserFields;
};

/**
 * Updates the current user's profile information.
 *
 * This function allows updating various user fields such as email, name,
 * and other profile information. Some updates may require OTP verification,
 * like email and phone number.
 *
 * @param params.userFields - The user fields to update.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to OTP verification details if verification is required, or undefined.
 */
export const updateUser = async (
  { userFields }: UpdateUserParams,
  client = getDefaultClient()
): Promise<OTPVerification | undefined> => {
  const core = getCore(client);
  const apiClient = createApiClient({ includeMfaToken: true }, client);

  const response = await apiClient.updateSelf({
    environmentId: core.environmentId,
    userFields,
  });

  updateAuthFromVerifyResponse({ response }, client);

  if (response.emailVerification) {
    return {
      email: response.emailVerification.email,
      verificationUUID: response.emailVerification.verificationUUID,
    };
  }

  if (response.smsVerification) {
    return {
      isoCountryCode: response.smsVerification.isoCountryCode,
      phoneCountryCode: response.smsVerification.phoneCountryCode,
      phoneNumber: response.smsVerification.phoneNumber,
      verificationUUID: response.smsVerification.verificationUUID,
    };
  }

  return undefined;
};
